import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { uiApi } from '@/api/ui.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { queryKeys, tierOptions } from '@/lib/query';
import type { UiScreenActions } from '@/types/ui';

const DEFAULT_ACTIONS: UiScreenActions = {
  path: '',
  canView: true,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canApprove: false,
  canReject: false,
  canExport: false,
  canImport: false,
  canProcess: false,
};

export type ResolvedScreenActions = UiScreenActions & { resolved: boolean };

/** Backend-driven screen actions — session cache → API. No client-side permission registry. */
export function useScreenActions(pathname?: string): ResolvedScreenActions {
  const location = useLocation();
  const path = pathname ?? location.pathname;
  const sessionActions = useAppSelector((s) => s.session.screenActions);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);

  const cached = sessionActions?.[path];

  const { data: fetched } = useQuery({
    queryKey: queryKeys.ui.screenActions(path),
    queryFn: () => uiApi.screenActions(path),
    enabled: sessionLoaded && !cached,
    ...tierOptions('operational'),
  });

  return useMemo(() => {
    const backend = cached ?? fetched;
    if (backend) {
      return { ...DEFAULT_ACTIONS, ...backend, path, resolved: true };
    }
    return {
      ...DEFAULT_ACTIONS,
      path,
      resolved: false,
    } satisfies ResolvedScreenActions;
  }, [cached, fetched, path]);
}
